import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton } from '@/components/documents';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  OkNote,
  Pager,
  ScreenWithForm,
  Select,
  StatusPill,
  TextArea,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatDate, formatMoney } from '@/format';
import {
  useBankAccounts,
  useCashBoxes,
  useExpenseCategories,
  useExpenses,
  useFinanceMutation,
  type ExpenseRow,
} from '@/hooks/finance';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

const PAGE = 50;

const DECISIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Waiting for a decision' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Sent back' },
];

/**
 * Financials › Farm Expenses.
 *
 * The one approval flow in the module, because an expense is the only place money
 * leaves with no document from outside to check it against. Raised by one person,
 * decided by another, committed by a third — and the server refuses each overlap,
 * so the buttons here go grey for the same reasons it would.
 *
 * Feed, medicine and fuel are not on the category list: buying them is a Stock IN,
 * and they only become a cost when they are used. Letting them through here would
 * count the same money twice.
 */
export function Expenses() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [approvalStatus, setApprovalStatus] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);
  const [deciding, setDeciding] = useState<{ row: ExpenseRow; approve: boolean } | null>(null);
  const [decisionNote, setDecisionNote] = useState('');

  const [farmId, setFarmId] = useState(farmIds[0] ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [expenseDate, setExpenseDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [paidFrom, setPaidFrom] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [billRef, setBillRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const expenses = useExpenses({ farmIds, approvalStatus, take: PAGE, skip });
  const categories = useExpenseCategories();
  const cashBoxes = useCashBoxes(farmIds);
  const banks = useBankAccounts(farmIds);
  const mutate = useFinanceMutation<unknown>();

  const rows = expenses.data?.items ?? [];
  const total = expenses.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  const places = useMemo(
    () => [
      { value: '', label: 'Not paid yet — put it on account' },
      ...(cashBoxes.data?.items ?? []).map((b) => ({
        value: `cash:${b.id}`,
        label: `${b.nameEn} — cash, ${b.farmName}`,
      })),
      ...(banks.data?.items ?? []).map((b) => ({
        value: `bank:${b.id}`,
        label: `${b.nameEn} — ${b.bankLabel ?? 'bank'}`,
      })),
    ],
    [cashBoxes.data, banks.data],
  );

  const spendable = (categories.data?.items ?? []).filter((c) => !c.isStockPurchase);

  function reset() {
    setEditing(null);
    setFarmId(farmIds[0] ?? '');
    setCategoryId('');
    setExpenseDate(todayIso());
    setAmount('');
    setPaidFrom('');
    setPaidTo('');
    setBillRef('');
    setRemarks('');
    setError('');
    setNote('');
  }

  function startEdit(row: ExpenseRow) {
    setEditing(row);
    setFarmId(row.farmId);
    setCategoryId(row.categoryId);
    setExpenseDate(row.expenseDate);
    setAmount(row.entryAmount);
    setPaidFrom(
      row.paidFromCashBoxId
        ? `cash:${row.paidFromCashBoxId}`
        : row.paidFromBankAccountId
          ? `bank:${row.paidFromBankAccountId}`
          : '',
    );
    setPaidTo(row.paidTo ?? '');
    setBillRef(row.billRef ?? '');
    setRemarks(row.remarks ?? '');
    setError('');
    setShowForm(true);
  }

  async function save() {
    setError('');
    setNote('');
    if (!farmId) return setError('Pick the farm');
    if (!categoryId) return setError('Say what the money was spent on');
    if (!amount || Number(amount) <= 0) return setError('Enter the amount');

    const [kind, id] = paidFrom.split(':');
    const body = {
      farmId,
      categoryId,
      expenseDate,
      amount,
      ...(kind === 'cash' && id ? { paidFromCashBoxId: id } : {}),
      ...(kind === 'bank' && id ? { paidFromBankAccountId: id } : {}),
      ...(paidTo.trim() ? { paidTo: paidTo.trim() } : {}),
      ...(billRef.trim() ? { billRef: billRef.trim() } : {}),
      ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
    };

    try {
      if (editing) {
        await mutate.mutateAsync(() =>
          api.patch(`/api/v1/fin/expenses/${editing.id}`, {
            version: editing.version,
            ...body,
          }),
        );
        setNote('Expense saved. It needs deciding on again.');
      } else {
        await mutate.mutateAsync(() => api.post('/api/v1/fin/expenses', body));
        setNote('Expense raised. Somebody else has to approve it.');
      }
      setShowForm(false);
      reset();
    } catch (e) {
      setError(errorMessage(e, 'That expense could not be saved'));
    }
  }

  async function decide() {
    if (!deciding) return;
    setError('');
    const { row, approve } = deciding;
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/fin/expenses/${row.id}/${approve ? 'approve' : 'reject'}`, {
          version: row.version,
          ...(decisionNote.trim() ? { note: decisionNote.trim() } : {}),
        }),
      );
      setNote(approve ? `${row.documentNo} approved.` : `${row.documentNo} sent back.`);
      setDeciding(null);
      setDecisionNote('');
    } catch (e) {
      setError(errorMessage(e, 'That decision could not be recorded'));
    }
  }

  async function post(row: ExpenseRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/fin/expenses/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.documentNo} posted.`);
    } catch (e) {
      setError(errorMessage(e, 'That expense could not be posted'));
    }
  }

  const columns: Array<GridColumn<ExpenseRow>> = [
    { key: 'expenseDate', header: 'Date', width: 104, render: (r) => formatDate(r.expenseDate) },
    { key: 'documentNo', header: 'Number', width: 136, render: (r) => r.documentNo },
    { key: 'farmName', header: 'Farm', width: 140, render: (r) => r.farmName },
    { key: 'categoryName', header: 'What for', width: 160, render: (r) => r.categoryName },
    {
      key: 'entryAmount',
      header: 'Amount',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.entryAmount),
    },
    {
      key: 'paidFromName',
      header: 'Paid from',
      width: 160,
      render: (r) =>
        r.paidFromName ?? (
          <span className="muted">{r.supplierName ? `on ${r.supplierName}` : 'not paid'}</span>
        ),
    },
    {
      key: 'approvalStatus',
      header: 'Decision',
      width: 130,
      render: (r) => <StatusPill status={r.approvalStatus} />,
    },
    { key: 'status', header: 'Status', width: 120, render: (r) => <StatusPill status={r.status} /> },
  ];

  const form = (
    <FormPanel
      title={editing ? `Expense ${editing.documentNo}` : 'Raise an expense'}
      icon={<AppIcon kind="advance" size={18} />}
      intro="Somebody else approves it, and a third person commits it."
    >
      <FormGroup title="What was spent" columns={2}>
        <Select
          label="Farm"
          value={farmId}
          onChange={setFarmId}
          options={farmIds.map((id) => ({ value: id, label: id }))}
          required
          disabled={Boolean(editing)}
        />
        <Select
          label="What for"
          value={categoryId}
          onChange={setCategoryId}
          options={spendable.map((c) => ({ value: c.id, label: c.nameEn }))}
          required
          hint="Feed, medicine and fuel are a Stock IN — they are not a cost until they are used."
        />
        <Field label="Date" type="date" value={expenseDate} onChange={setExpenseDate} required />
        <Field label="Amount" type="number" value={amount} onChange={setAmount} required />
      </FormGroup>

      <FormGroup title="How it was paid" columns={2}>
        <Select label="Paid from" value={paidFrom} onChange={setPaidFrom} options={places} />
        <Field
          label="Paid to"
          value={paidTo}
          onChange={setPaidTo}
          hint="Who took the money, where there is no supplier record."
        />
        <Field label="Bill reference" value={billRef} onChange={setBillRef} mono />
      </FormGroup>

      <FormGroup title="Note">
        <TextArea label="Note" value={remarks} onChange={setRemarks} />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Raise expense'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('fin.expense.create')}
      addLabel="Raise an expense"
      showForm={showForm}
      onToggle={() => {
        if (showForm) return setShowForm(false);
        reset();
        setShowForm(true);
      }}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <Select
          label="Decision"
          value={approvalStatus}
          onChange={(v) => {
            setApprovalStatus(v);
            setSkip(0);
          }}
          options={DECISIONS}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(
              current &&
                current.status === 'DRAFT' &&
                current.approvalStatus !== 'APPROVED' &&
                can('fin.expense.update'),
            ),
            reason:
              current?.approvalStatus === 'APPROVED'
                ? 'It has been approved; changing it would undo somebody’s decision'
                : 'Only a draft can be edited',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Approve',
            icon: 'approve',
            primary: true,
            enabled: Boolean(
              current &&
                current.status === 'DRAFT' &&
                current.approvalStatus === 'PENDING' &&
                // Whoever raised it cannot approve it. The list does not say who
                // raised what, so this is left to the server, whose refusal names
                // the reason — guessing here would grey out the button for the
                // wrong people.
                can('fin.expense.approve'),
            ),
            reason: 'Only one waiting for a decision can be approved',
            onSelect: () => current && setDeciding({ row: current, approve: true }),
          },
          {
            label: 'Send back',
            icon: 'reject',
            enabled: Boolean(
              current &&
                current.status === 'DRAFT' &&
                current.approvalStatus === 'PENDING' &&
                can('fin.expense.reject'),
            ),
            reason: 'Only one waiting for a decision can be sent back',
            onSelect: () => current && setDeciding({ row: current, approve: false }),
          },
          {
            label: 'Post',
            icon: 'post',
            enabled: Boolean(
              current &&
                current.status === 'DRAFT' &&
                current.approvalStatus === 'APPROVED' &&
                can('fin.expense.post'),
            ),
            reason: 'It has to be approved first',
            onSelect: () => current && void post(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && can('fin.expense.reverse') && (
          <ReverseButton
            label={`Reverse ${current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/fin/expenses/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Expense reversed.'))
                .catch((e: unknown) =>
                  setError(errorMessage(e, 'That expense could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="fin-expenses"
        label="Farm expenses"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => row.status === 'DRAFT' && startEdit(row)}
        empty="No expenses yet."
        loading={expenses.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        <span>
          {total} expense{total === 1 ? '' : 's'}
        </span>
      </StatusBar>

      {deciding && (
        <Modal
          title={
            deciding.approve
              ? `Approve ${deciding.row.documentNo}`
              : `Send ${deciding.row.documentNo} back`
          }
          onClose={() => {
            setDeciding(null);
            setDecisionNote('');
          }}
        >
          <p>
            {formatMoney(deciding.row.entryAmount)} for {deciding.row.categoryName} at{' '}
            {deciding.row.farmName}.
          </p>
          <TextArea
            label={deciding.approve ? 'Note' : 'Why it is going back'}
            value={decisionNote}
            onChange={setDecisionNote}
            required={!deciding.approve}
            hint={
              deciding.approve
                ? undefined
                : 'Whoever raised it has to know what to put right.'
            }
          />
          {error && <ErrorNote message={error} />}
          <FormActions
            onCancel={() => {
              setDeciding(null);
              setDecisionNote('');
            }}
          >
            <button
              type="button"
              className={`btn ${deciding.approve ? 'btn--primary' : 'btn--danger'}`}
              disabled={mutate.isPending || (!deciding.approve && !decisionNote.trim())}
              onClick={() => void decide()}
            >
              {deciding.approve ? 'Approve' : 'Send back'}
            </button>
          </FormActions>
        </Modal>
      )}
    </ScreenWithForm>
  );
}
